import React, { useEffect, useMemo, useRef, useState } from 'react'
import type { SpreadsheetRangeAddress } from '../../protocol/spreadsheetEngineProtocol'
import {
  buildValidationListPickerPresentation,
  getValidationListPickerDefaultFocusedIndex,
} from '../../spreadsheet/validationPresentation'

export interface ExcelValidationListPickerProps {
  x: number
  y: number
  selectionLabel: string
  values: string[]
  currentValue?: string | null
  sourceFormula?: string | null
  sourceRanges?: SpreadsheetRangeAddress[]
  error?: string | null
  onSelect: (value: string) => void
  onEditRule: () => void
  onClose: () => void
}

export const ExcelValidationListPicker: React.FC<ExcelValidationListPickerProps> = ({
  x,
  y,
  selectionLabel,
  values,
  currentValue,
  sourceFormula,
  sourceRanges,
  error,
  onSelect,
  onEditRule,
  onClose,
}) => {
  const rootRef = useRef<HTMLDivElement>(null)
  const filterRef = useRef<HTMLInputElement>(null)
  const [filter, setFilter] = useState('')
  const [focusedIndex, setFocusedIndex] = useState(0)
  const defaultFocusedIndex = useMemo(() => {
    return getValidationListPickerDefaultFocusedIndex({ values, currentValue, filter })
  }, [currentValue, filter, values])
  const presentation = useMemo(() => buildValidationListPickerPresentation({
    selectionLabel,
    values,
    currentValue,
    sourceFormula,
    sourceRanges,
    error,
    filter,
    focusedIndex,
  }), [currentValue, error, filter, focusedIndex, selectionLabel, sourceFormula, sourceRanges, values])

  useEffect(() => {
    setFocusedIndex(defaultFocusedIndex)
  }, [defaultFocusedIndex])

  useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
      if (rootRef.current?.contains(event.target as Node)) return
      onClose()
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [onClose])

  useEffect(() => {
    filterRef.current?.focus()
  }, [])

  const chooseValue = (value: string) => {
    onSelect(value)
    onClose()
  }

  return (
    <div
      ref={rootRef}
      className="fg-excel-validation-list-picker"
      data-testid="fg-excel-validation-list-picker"
      role="dialog"
      aria-label={presentation.dialogLabel}
      title={presentation.dialogTitle}
      onMouseDown={(event) => {
        event.stopPropagation()
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault()
          event.stopPropagation()
          onClose()
          return
        }
        if (!presentation.filteredValues.length) return
        if (event.key === 'ArrowDown') {
          event.preventDefault()
          event.stopPropagation()
          setFocusedIndex((current) => Math.min(presentation.filteredValues.length - 1, current + 1))
          return
        }
        if (event.key === 'ArrowUp') {
          event.preventDefault()
          event.stopPropagation()
          setFocusedIndex((current) => Math.max(0, current - 1))
          return
        }
        if (event.key === 'Home') {
          event.preventDefault()
          event.stopPropagation()
          setFocusedIndex(0)
          return
        }
        if (event.key === 'End') {
          event.preventDefault()
          event.stopPropagation()
          setFocusedIndex(presentation.filteredValues.length - 1)
          return
        }
        if (event.key === 'Enter') {
          event.preventDefault()
          event.stopPropagation()
          const value = presentation.filteredValues[focusedIndex]
          if (value) chooseValue(value)
        }
      }}
      style={{
        position: 'fixed',
        left: x,
        top: y,
        zIndex: 1100,
        width: 244,
        maxWidth: 'calc(100vw - 16px)',
        maxHeight: 'min(360px, calc(100vh - 24px))',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        border: '1px solid rgba(148, 163, 184, 0.74)',
        borderRadius: 6,
        background: '#ffffff',
        boxShadow: '0 14px 32px rgba(15, 23, 42, 0.22)',
        color: '#111827',
        fontFamily: 'Arial, sans-serif',
        fontSize: 12,
      }}
    >
      <div
        style={{
          padding: '7px 9px 6px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <div style={{ fontWeight: 750, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {presentation.selectionLabel}
        </div>
        <button
          type="button"
          aria-label={presentation.closeLabel}
          title={presentation.closeTitle}
          onClick={onClose}
          style={{
            width: 20,
            height: 20,
            border: '1px solid transparent',
            borderRadius: 4,
            background: 'transparent',
            color: '#4b5563',
            cursor: 'pointer',
            fontWeight: 800,
            lineHeight: '18px',
            padding: 0,
          }}
        >
          x
        </button>
      </div>

      {presentation.filterVisible ? (
        <div style={{ padding: '7px 8px 6px', borderBottom: '1px solid #eef2f7' }}>
          <input
            ref={filterRef}
            type="text"
            aria-label={presentation.filterLabel}
            title={presentation.filterTitle}
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            placeholder={presentation.filterPlaceholder}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              height: 28,
              border: '1px solid #cbd5e1',
              borderRadius: 4,
              padding: '0 8px',
              fontSize: 12,
              outline: 'none',
            }}
          />
        </div>
      ) : null}

      <div
        role="listbox"
        aria-label={presentation.listboxLabel}
        style={{
          overflowY: 'auto',
          maxHeight: 236,
          padding: presentation.hasValues ? '3px 0' : '8px',
        }}
      >
        {presentation.options.length > 0 ? (
          presentation.options.map((option, index) => {
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={option.isCurrent}
                title={option.title}
                onMouseEnter={() => setFocusedIndex(index)}
                onClick={() => chooseValue(option.value)}
                style={{
                  width: '100%',
                  height: 28,
                  border: 0,
                  background: option.isFocused ? '#eaf4ef' : option.isCurrent ? '#f0fdf4' : '#ffffff',
                  color: '#111827',
                  cursor: 'pointer',
                  display: 'grid',
                  gridTemplateColumns: '22px minmax(0, 1fr) auto',
                  alignItems: 'center',
                  gap: 6,
                  padding: '0 9px',
                  textAlign: 'left',
                  fontSize: 12,
                }}
                >
                <span aria-hidden="true" style={{ color: 'var(--fg-green)', fontWeight: 800 }}>
                  {option.isCurrent ? '*' : ''}
                </span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{option.label}</span>
                {option.currentLabel ? (
                  <span
                    style={{
                      color: 'var(--fg-green)',
                      fontSize: 11,
                      fontWeight: 750,
                    }}
                  >
                    {option.currentLabel}
                  </span>
                ) : null}
              </button>
            )
          })
        ) : (
          <div style={{ color: '#6b7280', lineHeight: 1.35 }}>
            {presentation.emptyMessage}
          </div>
        )}
      </div>

      <div
        style={{
          borderTop: '1px solid #e5e7eb',
          padding: '6px 8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <div
          title={presentation.statusTitle}
          style={{
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: presentation.statusIsError ? '#991b1b' : '#6b7280',
          }}
        >
          {presentation.statusText}
        </div>
        <button
          type="button"
          title={presentation.editRuleTitle}
          onClick={() => {
            onEditRule()
            onClose()
          }}
          style={{
            flex: '0 0 auto',
            height: 24,
            border: '1px solid #cbd5e1',
            borderRadius: 4,
            background: '#ffffff',
            color: 'var(--fg-green)',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 700,
            padding: '0 8px',
          }}
        >
          {presentation.editRuleLabel}
        </button>
      </div>
    </div>
  )
}
